import { ReactNode, FunctionComponent, useState } from "react";
import {
  Chip,
  useMediaQuery,
  useTheme,
  IconButton,
  Collapse,
  Box,
} from "@mui/material";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import { tokens } from "../../theme";

interface BorderedSectionProps {
  icon?: JSX.Element;
  title?: string | JSX.Element;
  children: ReactNode;
  subTitle?: string;
  hasCollapse?: boolean;
  defaultExpanded?: boolean;
  borderBox?: boolean;
}

const BorderedSection: FunctionComponent<BorderedSectionProps> = ({
  icon,
  title,
  children,
  subTitle,
  hasCollapse = false,
  defaultExpanded = false,
  borderBox = false,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [expanded, setExpanded] = useState(
    hasCollapse ? defaultExpanded : true
  );

  const getBorderColor = () => {
    return theme.palette.mode === "dark"
      ? borderBox ? tokens(theme.palette.mode).primary[500] : "rgb(123, 123, 123)"
      : borderBox ? tokens(theme.palette.mode).grey[700] : "#252525";
  };

  const borderThickness = borderBox ? "3px" : "1px";
  const borderColor = getBorderColor();
  const borderStyle = `${borderThickness} solid ${borderColor}`;

  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
        maxWidth: "100%",
        borderRadius: "5px",
        borderLeft: borderStyle,
        borderBottom: borderStyle,
        borderRight: borderStyle,
      }}
    >
      {/* Header row */}
      <Box sx={{ display: "flex", flexDirection: "row", width: "100%" }}>
        {hasCollapse && (
          <IconButton
            onClick={() => setExpanded(!expanded)}
            sx={{ position: "absolute", top: "5px", left: "5px" }}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}

        {/* Left border cap */}
        <Box
          sx={{
            width: "1em",
            borderTop: borderStyle,
            borderTopLeftRadius: "5px",
          }}
        />

        {/* Title area */}
        {(icon || title) && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              alignItems: "center",
              gap: "0.25em",
              width: "fit-content",
              height: "2em",
              mx: "0.5em",
              mt: "-1em",
              mb: expanded ? "0px" : "30px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              fontSize: "1em",
              fontWeight: 600,
              transition: "margin-bottom 0.5s ease-in-out",
            }}
          >
            {icon || ""}
            {title && <span>{title}</span>}
            {subTitle && (
              <Chip
                sx={{
                  backgroundColor: colors.primary[500],
                  color: colors.success[300],
                  fontWeight: "bold",
                }}
                label={
                  isMobile ? (
                    <Box
                      sx={{
                        position: "relative",
                        overflow: "hidden",
                        display: "inline-block",
                        width: "100%",
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          display: "inline-block",
                          whiteSpace: "nowrap",
                          animation: "slideText 8s linear infinite",
                          "@keyframes slideText": {
                            "0%": { transform: "translateX(100%)" },
                            "100%": { transform: "translateX(-100%)" },
                          },
                        }}
                      >
                        {subTitle}
                      </Box>
                    </Box>
                  ) : (
                    subTitle
                  )
                }
              />
            )}
          </Box>
        )}

        {hasCollapse && (
          <IconButton
            onClick={() => setExpanded(!expanded)}
            sx={{ position: "absolute", top: "5px", right: "5px" }}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        )}

        {/* Right border cap */}
        <Box
          sx={{
            width: "1em",
            flexGrow: 2,
            borderTop: borderStyle,
            borderTopRightRadius: "5px",
          }}
        />
      </Box>

      {/* Content */}
      {hasCollapse ? (
        <Collapse in={expanded}>
          <Box sx={{ p: "1em" }}>{children}</Box>
        </Collapse>
      ) : (
        <Box sx={{ p: "1em" }}>{children}</Box>
      )}
    </Box>
  );
};

export default BorderedSection;
